import axios from 'axios';

// Determine if we're in development or production
const isDev = process.env.NODE_ENV === 'development';

// API key access depends on environment
const API_KEY = isDev ? process.env.REACT_APP_TMDB_API_KEY : undefined;

// Set base URL based on environment
const BASE_URL = isDev 
  ? 'https://api.themoviedb.org/3' 
  : '/api';

// Create an axios instance
const tmdbApi = axios.create({
  baseURL: BASE_URL
});

// Get configuration data
export const getConfig = async () => {
  try {
    // In development, call TMDB directly
    if (isDev) {
      const response = await axios.get(`${BASE_URL}/configuration`, {
        params: { api_key: API_KEY }
      });
      return response.data;
    } 
    // In production, use serverless function
    else {
      const response = await axios.get('/api/config');
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching TMDb config:', error);
    throw error;
  }
};

// Get currently playing movies
export const getNowPlayingMovies = async (page = 1) => {
  try {
    let response;
    
    // In development, call TMDB directly
    if (isDev) {
      response = await axios.get(`${BASE_URL}/movie/now_playing`, {
        params: {
          api_key: API_KEY,
          page,
          region: 'US'
        }
      });
    } 
    // In production, use serverless function
    else {
      response = await axios.get('/api/now-playing', {
        params: { page }
      });
    }
    
    // Filter movies from the past 2 months
    const twoMonthsAgo = getDateFromMonthsAgo(2);
    const filteredResults = response.data.results.filter(movie => {
      const releaseDate = new Date(movie.release_date);
      return releaseDate >= twoMonthsAgo;
    });
    
    return {
      ...response.data,
      results: filteredResults
    };
  } catch (error) {
    console.error('Error fetching now playing movies:', error);
    throw error;
  }
};

// Get upcoming movies - similar pattern
export const getUpcomingMovies = async (page = 1) => {
  try {
    let response;
    
    // In development, call TMDB directly
    if (isDev) {
      response = await axios.get(`${BASE_URL}/movie/upcoming`, {
        params: {
          api_key: API_KEY,
          page,
          region: 'US'
        }
      });
    } 
    // In production, use serverless function
    else {
      response = await axios.get('/api/upcoming', {
        params: { page }
      });
    }
    
    // Filter out movies that have already been released
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const filteredResults = response.data.results.filter(movie => {
      const releaseDate = new Date(movie.release_date);
      return releaseDate >= today;
    });
    
    return {
      ...response.data,
      results: filteredResults
    };
  } catch (error) {
    console.error('Error fetching upcoming movies:', error);
    throw error;
  }
};

// Get movie details by ID
export const getMovieDetails = async (movieId) => {
  try {
    let response;
    
    // In development, call TMDB directly
    if (isDev) {
      response = await axios.get(`${BASE_URL}/movie/${movieId}`, {
        params: {
          api_key: API_KEY,
          append_to_response: 'videos,credits'
        }
      });
    } 
    // In production, use serverless function
    else {
      response = await axios.get('/api/movie', {
        params: { id: movieId }
      });
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error fetching movie details for ID ${movieId}:`, error);
    throw error;
  }
};

// Utility functions remain the same
export const getImageUrl = (path, size = 'original') => {
  if (!path) return null;
  return `https://image.tmdb.org/t/p/${size}${path}`;
};

export const getDateFromMonthsAgo = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date;
};

// Export default object
export default {
  getNowPlayingMovies,
  getUpcomingMovies,
  getMovieDetails,
  getConfig,
  getImageUrl,
  getDateFromMonthsAgo,
};